import { Injectable, NgZone } from '@angular/core';
import { Router, NavigationStart } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

interface ProductSheetState {
  isOpen: boolean;
  productId: number | null;
  /**
   * The cart line the sheet should open pointed at, or null for a plain "show me this dish" open.
   *
   * Travels beside `productId` rather than instead of it because a product id no longer identifies
   * a cart row: once add-ons exist one dish can occupy several lines, and a cart row that wants the
   * sheet to EDIT it has to say which one it means.
   */
  editingLineKey: string | null;
}

/**
 * Drives the global product-details bottom sheet. The sheet is hosted once in
 * AppComponent above the router-outlet, so it overlays whatever screen is
 * currently mounted (menu / category / order-details) — the previous screen
 * stays visible and dimmed behind it, matching the iOS `.sheet` presentation.
 *
 * In-app opens call `open(id)` (no route change). The `product-details/:id`
 * route is kept only for deep-links, handled by the routed component instance.
 *
 * Because opening no longer changes the route, this service takes over the two
 * responsibilities the router used to provide:
 *   - Back button: `open()` pushes a synthetic history entry so the hardware/
 *     browser Back closes the sheet instead of leaving the menu.
 *   - Programmatic navigation (e.g. a push-notification deep-link) must tear the
 *     sheet down, otherwise the overlay + body scroll-lock persist over the new
 *     screen and the app looks frozen.
 */
@Injectable({ providedIn: 'root' })
export class ProductSheetService {
  private state = new BehaviorSubject<ProductSheetState>({
    isOpen: false,
    productId: null,
    editingLineKey: null
  });

  /** Emits on every open/close; AppComponent binds this with the async pipe. */
  readonly changes$: Observable<ProductSheetState> = this.state.asObservable();

  /** True while our synthetic history entry is on the stack (so Back closes the sheet). */
  private hasHistoryEntry = false;

  constructor(private router: Router, private ngZone: NgZone) {
    // Hardware/browser Back: the browser has already popped our synthetic entry,
    // so just close the sheet in place (do not push/pop history again).
    window.addEventListener('popstate', this.onPopState);

    // Any programmatic route change while the sheet is open must close it; the
    // router is taking over history, so we must not pop our own entry here.
    this.router.events
      .pipe(filter(e => e instanceof NavigationStart))
      .subscribe(() => {
        if (this.isOpen) {
          this.hasHistoryEntry = false;
          this.reset();
        }
      });
  }

  get isOpen(): boolean {
    return this.state.value.isOpen;
  }

  get productId(): number | null {
    return this.state.value.productId;
  }

  /** The line the sheet was opened to edit, or null. Exposed exactly as `productId` is. */
  get editingLineKey(): string | null {
    return this.state.value.editingLineKey;
  }

  /**
   * Open the sheet over the current screen.
   *
   * `editingLineKey` points the sheet at one existing cart row — the cart's rows pass it so tapping
   * "Pizza + bacon" opens on THAT line rather than on a blank picker that would silently create a
   * third combination. Omitting it is the pre-add-ons meaning and every existing caller keeps it.
   */
  open(productId: number, editingLineKey?: string): void {
    document.body.classList.add('pd-sheet-open');
    // Push a same-URL history entry once per open session so Back closes the sheet.
    if (!this.hasHistoryEntry) {
      history.pushState({ pdSheet: true }, '', location.href);
      this.hasHistoryEntry = true;
    }
    this.state.next({ isOpen: true, productId, editingLineKey: editingLineKey ?? null });
  }

  /**
   * User-initiated close (X / backdrop). Pops the synthetic history entry we
   * pushed on open so the history stack is left as it was before opening. The
   * resulting popstate is a no-op because `isOpen` is already false by then.
   */
  close(): void {
    const hadEntry = this.hasHistoryEntry;
    this.reset();
    if (hadEntry) {
      history.back();
    }
  }

  /**
   * Close the sheet ahead of a programmatic navigation. Returns true when a
   * synthetic history entry was on top, signalling the caller to navigate with
   * `{ replaceUrl: true }` so the destination overwrites that entry instead of
   * pushing on top of it (which would orphan a duplicate entry behind the new
   * route). Callers must use this rather than relying on the NavigationStart
   * hook below, because a same-URL target emits no NavigationStart
   * (onSameUrlNavigation: 'ignore'), leaving the overlay + scroll-lock stuck.
   */
  closeForNavigation(): boolean {
    if (!this.isOpen) return false;
    this.reset();
    return true;
  }

  private onPopState = (): void => {
    if (!this.isOpen) return;
    this.ngZone.run(() => {
      this.hasHistoryEntry = false;
      this.reset();
    });
  };

  /** Clear sheet state (and body scroll-lock) without touching history. */
  private reset(): void {
    this.hasHistoryEntry = false;
    document.body.classList.remove('pd-sheet-open');
    this.state.next({ isOpen: false, productId: null, editingLineKey: null });
  }
}
